import { Box, Button, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Check, ChevronDown, ChevronUp, CornerDownRight, Pencil, Plus, Trash2, Video, X } from "lucide-react";
import { useState } from "react";
import type { TrainingSection, TrainingTopic } from "@xop/dashboard-shared";

import { formatDuration, moveTopic } from "./trainingLogic.js";

export interface TopicMenuActions {
  onSelect: (id: number) => void;
  onAddSection: (title: string) => Promise<void>;
  onAddLesson: (sectionId: number, title: string) => Promise<void>;
  onRename: (id: number, title: string) => Promise<void>;
  onMove: (id: number, dir: "up" | "down") => void;
  onDelete: (topic: TrainingTopic, lessonCount: number) => void;
}

/**
 * The module's topic menu, as the reseller will see it — exactly two levels:
 * SECTIONS, and LESSONS indented under them. Every row is selectable (it
 * opens the topic panel); an editor also gets inline rename, move up/down
 * among its siblings (a section carries its lessons with it), delete, and
 * "add lesson" on a section. Adding is inline too: type, Enter.
 *
 * The component is controlled — it owns only the transient UI (which row is
 * being renamed, which add box is open); every change goes through the page,
 * which writes it to portal-api and replaces the menu with the answer.
 */
export function TopicMenuEditor({
  sections,
  selectedId,
  canEdit,
  busy,
  actions,
}: {
  sections: TrainingSection[];
  selectedId: number | null;
  canEdit: boolean;
  busy: boolean;
  actions: TopicMenuActions;
}): JSX.Element {
  const [renaming, setRenaming] = useState<number | null>(null);
  const [addingLessonTo, setAddingLessonTo] = useState<number | null>(null);

  const row = (topic: TrainingTopic, opts: { lesson: boolean; lessonCount: number; number: string }): JSX.Element => {
    const selected = topic.id === selectedId;
    return (
      <Box
        key={topic.id}
        data-testid={`topic-row-${topic.id}`}
        data-kind={opts.lesson ? "lesson" : "section"}
        data-selected={selected ? "true" : "false"}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          pl: opts.lesson ? 3.5 : 1,
          pr: 0.5,
          py: 0.5,
          borderRadius: 1,
          cursor: "pointer",
          bgcolor: (t) => (selected ? alpha(t.palette.primary.main, 0.08) : "transparent"),
          outline: (t) => (selected ? `1px solid ${alpha(t.palette.primary.main, 0.35)}` : "none"),
          "&:hover": { bgcolor: (t) => (selected ? alpha(t.palette.primary.main, 0.1) : t.palette.action.hover) },
          "&:hover .topic-row-tools, &:focus-within .topic-row-tools": { opacity: 1 },
        }}
        onClick={() => {
          if (renaming !== topic.id) actions.onSelect(topic.id);
        }}
      >
        {opts.lesson && (
          <Box component="span" sx={{ color: "text.disabled", display: "inline-flex", ml: -2.25, mr: 0.25 }}>
            <CornerDownRight size={14} />
          </Box>
        )}
        <Typography
          component="span"
          sx={{ fontSize: 12, color: "text.disabled", fontVariantNumeric: "tabular-nums", minWidth: opts.lesson ? 28 : 18 }}
        >
          {opts.number}
        </Typography>

        {renaming === topic.id ? (
          <RenameBox
            initial={topic.title}
            onCancel={() => setRenaming(null)}
            onSave={async (title) => {
              await actions.onRename(topic.id, title);
              setRenaming(null);
            }}
          />
        ) : (
          <>
            <Typography
              component="span"
              sx={{
                flex: 1,
                minWidth: 0,
                fontSize: 14,
                fontWeight: opts.lesson ? 400 : 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              data-testid="topic-row-title"
            >
              {topic.title}
            </Typography>
            <VideoBadge topic={topic} />
            {canEdit && (
              <Box
                className="topic-row-tools"
                sx={{ display: "flex", alignItems: "center", opacity: { xs: 1, md: selected ? 1 : 0 }, transition: "opacity 120ms" }}
                onClick={(e) => e.stopPropagation()}
              >
                <Tool label="Move up" testId="topic-move-up" disabled={busy || !moveTopic(sections, topic.id, "up")} onClick={() => actions.onMove(topic.id, "up")}>
                  <ChevronUp size={16} />
                </Tool>
                <Tool label="Move down" testId="topic-move-down" disabled={busy || !moveTopic(sections, topic.id, "down")} onClick={() => actions.onMove(topic.id, "down")}>
                  <ChevronDown size={16} />
                </Tool>
                <Tool label="Rename" testId="topic-rename" disabled={busy} onClick={() => setRenaming(topic.id)}>
                  <Pencil size={14} />
                </Tool>
                {!opts.lesson && (
                  <Tool label="Add a lesson" testId="topic-add-lesson" disabled={busy} onClick={() => setAddingLessonTo(topic.id)}>
                    <Plus size={16} />
                  </Tool>
                )}
                <Tool label="Delete" testId="topic-delete" disabled={busy} onClick={() => actions.onDelete(topic, opts.lessonCount)}>
                  <Trash2 size={14} />
                </Tool>
              </Box>
            )}
          </>
        )}
      </Box>
    );
  };

  return (
    <Box data-testid="topic-menu" sx={{ display: "grid", gap: 0.25 }}>
      {sections.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1.5 }}>
          {canEdit
            ? "No topics yet. Add a section — say “Getting started” — then add lessons under it."
            : "No topics yet."}
        </Typography>
      )}

      {sections.map((s, i) => (
        <Box key={s.id} data-testid={`topic-section-${s.id}`} sx={{ display: "grid", gap: 0.25 }}>
          {row(s, { lesson: false, lessonCount: s.lessons.length, number: `${i + 1}` })}
          {s.lessons.map((l, j) => row(l, { lesson: true, lessonCount: 0, number: `${i + 1}.${j + 1}` }))}
          {canEdit && addingLessonTo === s.id && (
            <Box sx={{ pl: 3.5 }}>
              <AddBox
                placeholder="Lesson title"
                testId="add-lesson-input"
                onCancel={() => setAddingLessonTo(null)}
                onAdd={async (title) => {
                  await actions.onAddLesson(s.id, title);
                  setAddingLessonTo(null);
                }}
              />
            </Box>
          )}
        </Box>
      ))}

      {canEdit && <AddSection busy={busy} onAdd={actions.onAddSection} />}
    </Box>
  );
}

function VideoBadge({ topic }: { topic: TrainingTopic }): JSX.Element | null {
  if (!topic.video) return null;
  return (
    <Tooltip title={topic.video.kind === "file" ? "Uploaded video" : "Linked video"}>
      <Box
        component="span"
        data-testid="topic-has-video"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: "text.secondary", fontSize: 12, flexShrink: 0 }}
      >
        <Video size={14} />
        {topic.duration_seconds ? formatDuration(topic.duration_seconds) : null}
      </Box>
    </Tooltip>
  );
}

function Tool({
  label,
  testId,
  disabled,
  onClick,
  children,
}: {
  label: string;
  testId: string;
  disabled: boolean;
  onClick: () => void;
  children: JSX.Element;
}): JSX.Element {
  return (
    <Tooltip title={label} disableInteractive>
      <span>
        <IconButton size="small" aria-label={label} data-testid={testId} disabled={disabled} onClick={onClick} sx={{ p: 0.5 }}>
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function RenameBox({
  initial,
  onSave,
  onCancel,
}: {
  initial: string;
  onSave: (title: string) => Promise<void>;
  onCancel: () => void;
}): JSX.Element {
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const save = async (): Promise<void> => {
    const title = value.trim();
    if (!title || title === initial) return onCancel();
    setSaving(true);
    try {
      await onSave(title);
    } catch {
      // The page already said what went wrong; keep the box open to retry.
    } finally {
      setSaving(false);
    }
  };
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1 }} onClick={(e) => e.stopPropagation()}>
      <TextField
        size="small"
        value={value}
        autoFocus
        disabled={saving}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void save();
          if (e.key === "Escape") onCancel();
        }}
        inputProps={{ "data-testid": "topic-rename-input", "aria-label": "Topic title" }}
        sx={{ flex: 1 }}
      />
      <IconButton size="small" aria-label="Save title" onClick={() => void save()} disabled={saving} data-testid="topic-rename-save">
        <Check size={16} />
      </IconButton>
      <IconButton size="small" aria-label="Cancel" onClick={onCancel} disabled={saving}>
        <X size={16} />
      </IconButton>
    </Box>
  );
}

function AddBox({
  placeholder,
  testId,
  onAdd,
  onCancel,
}: {
  placeholder: string;
  testId: string;
  onAdd: (title: string) => Promise<void>;
  onCancel: () => void;
}): JSX.Element {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const add = async (): Promise<void> => {
    const title = value.trim();
    if (!title) return;
    setSaving(true);
    try {
      await onAdd(title);
      setValue("");
    } catch {
      // The page already said what went wrong; keep what was typed.
    } finally {
      setSaving(false);
    }
  };
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, py: 0.5 }}>
      <TextField
        size="small"
        value={value}
        autoFocus
        disabled={saving}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void add();
          if (e.key === "Escape") onCancel();
        }}
        inputProps={{ "data-testid": testId, "aria-label": placeholder }}
        sx={{ flex: 1 }}
      />
      <Button size="small" variant="contained" disabled={saving || !value.trim()} onClick={() => void add()} data-testid={`${testId}-submit`}>
        Add
      </Button>
      <IconButton size="small" aria-label="Cancel" onClick={onCancel} disabled={saving}>
        <X size={16} />
      </IconButton>
    </Box>
  );
}

function AddSection({ busy, onAdd }: { busy: boolean; onAdd: (title: string) => Promise<void> }): JSX.Element {
  const [open, setOpen] = useState(false);
  if (open) {
    return (
      <Box sx={{ px: 1, pt: 0.5 }}>
        <AddBox
          placeholder="Section title"
          testId="add-section-input"
          onCancel={() => setOpen(false)}
          onAdd={async (title) => {
            await onAdd(title);
            setOpen(false);
          }}
        />
      </Box>
    );
  }
  return (
    <Box sx={{ pt: 1 }}>
      <Button
        size="small"
        startIcon={<Plus size={16} />}
        onClick={() => setOpen(true)}
        disabled={busy}
        data-testid="add-section"
        sx={{ color: "text.secondary" }}
      >
        Add section
      </Button>
    </Box>
  );
}
